import React, { useId, useRef } from "react";
import styles from "./QuantityTiersField.module.css";

// ─── Types ────────────────────────────────────────────────────────────────────
// Defined locally so @codway-dev/ui stays free of @codway-dev/shared dependencies.
// The shared package exports structurally-compatible types that consumers spread
// into these props.

export interface QuantityTierStyles {
  borderColor?: string;
  activeBorderColor?: string;
}

export interface QuantityTierBadge {
  text: string;
  textColor?: string;
  backgroundColor?: string;
}

export interface QuantityTierItem {
  id: string;
  quantity: number;
  title: string;
  priceLabel: string;
  compareAtLabel?: string;
  badge?: QuantityTierBadge;
  imageUrl?: string;
  styles?: QuantityTierStyles;
  isDefault: boolean;
  priceColor?: string;
  titleColor?: string;
}

export interface QuantityTierVariantOption {
  id: string;
  title: string;
}

export interface QuantityTiersFieldProps {
  tiers: QuantityTierItem[];
  selectedIndex: number | null;
  onSelect: (index: number | null) => void;
  layout?: "horizontal" | "grid";
  disabled?: boolean;
  backgroundColor?: string;
  // Background applied to the selected tier. Falls back to the
  // form's `--cod-field-bg-color` when omitted, keeping the previous
  // behavior for offers that haven't been re-saved.
  selectedBackgroundColor?: string;
  // Border color for the active/selected tier — also used as the
  // radio's selected ring + inner dot. Sets `--qt-active-border-color`
  // at the wrapper. The per-tier `styles.activeBorderColor` still
  // works and takes precedence at the tier scope.
  activeBorderColor?: string;
  borderRadius?: number;
  // When provided (and length > 1), the selected tier shows N variant
  // dropdowns inside its card, where N = tier.quantity.
  variants?: QuantityTierVariantOption[];
  hideVariantNumbers?: boolean;
  disableVariants?: boolean;
  // Optional controlled selection — keyed by slot index (0..quantity-1).
  variantSelections?: Record<number, string>;
  onVariantChange?: (slotIndex: number, variantId: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function tierCssVars(tier: QuantityTierItem): React.CSSProperties {
  if (!tier.styles) return {};
  return {
    "--qt-border-color": tier.styles.borderColor,
    "--qt-active-border-color": tier.styles.activeBorderColor,
  } as React.CSSProperties;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const QuantityTiersField: React.FC<QuantityTiersFieldProps> = ({
  tiers,
  selectedIndex,
  onSelect,
  layout = "horizontal",
  disabled = false,
  backgroundColor,
  selectedBackgroundColor,
  activeBorderColor,
  borderRadius,
  variants,
  hideVariantNumbers = false,
  disableVariants = false,
  variantSelections,
  onVariantChange,
}) => {
  const hasVariants = !!variants && variants.length > 1;
  const groupId = useId();
  const wrapperVars: React.CSSProperties = {
    ...(backgroundColor
      ? ({ "--qt-background-color": backgroundColor } as React.CSSProperties)
      : {}),
    ...(selectedBackgroundColor
      ? ({
          "--qt-active-bg-color": selectedBackgroundColor,
        } as React.CSSProperties)
      : {}),
    ...(activeBorderColor
      ? ({
          "--qt-active-border-color": activeBorderColor,
        } as React.CSSProperties)
      : {}),
    ...(borderRadius != null
      ? ({ "--qt-border-radius": `${borderRadius}px` } as React.CSSProperties)
      : {}),
  };
  const tierRefs = useRef<(HTMLDivElement | null)[]>([]);

  const renderVariantDropdowns = (tier: QuantityTierItem) => {
    if (!hasVariants || disableVariants) return null;
    const rows = Math.max(1, tier.quantity);
    return (
      <div
        className={styles.variantSection}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {Array.from({ length: rows }).map((_, slot) => (
          <div key={slot} className={styles.variantRow}>
            {!hideVariantNumbers && (
              <span className={styles.variantNumber}>{slot + 1}.</span>
            )}
            <select
              className={styles.variantSelect}
              disabled={disabled}
              value={variantSelections?.[slot] ?? variants![0].id}
              onChange={(e) => onVariantChange?.(slot, e.target.value)}
            >
              {variants!.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.title}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      next = (index + 1) % tiers.length;
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      next = (index - 1 + tiers.length) % tiers.length;
    }
    if (next !== null) {
      e.preventDefault();
      onSelect(next);
      tierRefs.current[next]?.focus();
    }
  };

  const handleClick = (index: number) => {
    if (disabled) return;
    // Clicking the selected tier deselects it.
    onSelect(selectedIndex === index ? null : index);
  };

  // ── Grid layout ─────────────────────────────────────────────────────────────
  if (layout === "grid") {
    return (
      <div
        className={styles.wrapperGrid}
        role="radiogroup"
        aria-labelledby={`qt-group-${groupId}`}
        data-disabled={disabled || undefined}
        style={wrapperVars}
      >
        {tiers.map((tier, i) => {
          const isSelected = selectedIndex === i;
          return (
            <div
              key={tier.id}
              ref={(el) => {
                tierRefs.current[i] = el;
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              className={isSelected ? styles.tierGridSelected : styles.tierGrid}
              style={tierCssVars(tier)}
              onClick={() => handleClick(i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            >
              {tier.imageUrl && (
                <img
                  src={tier.imageUrl}
                  alt=""
                  className={styles.imageGrid}
                  aria-hidden="true"
                />
              )}

              <div className={styles.contentGrid}>
                {/* Radio indicator — top-right inside content area */}
                <div
                  className={
                    isSelected ? styles.radioGridSelected : styles.radioGrid
                  }
                >
                  {isSelected && <span className={styles.radioInner} />}
                </div>

                <span
                  className={styles.title}
                  style={
                    tier.titleColor ? { color: tier.titleColor } : undefined
                  }
                >
                  {tier.title}
                </span>

                <div className={styles.priceRow}>
                  <span
                    className={styles.price}
                    style={
                      tier.priceColor ? { color: tier.priceColor } : undefined
                    }
                  >
                    {tier.priceLabel}
                  </span>
                  {tier.compareAtLabel && (
                    <span className={styles.compareAt}>
                      {tier.compareAtLabel}
                    </span>
                  )}
                </div>
              </div>

              {tier.badge && (
                <div
                  className={styles.badgeGrid}
                  style={{
                    color: tier.badge.textColor ?? "#fff",
                    backgroundColor:
                      tier.badge.backgroundColor ??
                      "var(--cod-primary-color, #000)",
                  }}
                >
                  {tier.badge.text}
                </div>
              )}

              {isSelected && renderVariantDropdowns(tier)}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Horizontal layout (default) ─────────────────────────────────────────────
  return (
    <div
      className={styles.wrapperHorizontal}
      role="radiogroup"
      aria-labelledby={`qt-group-${groupId}`}
      data-disabled={disabled || undefined}
      style={wrapperVars}
    >
      {tiers.map((tier, i) => {
        const isSelected = selectedIndex === i;
        // When there is no image but a badge exists, the badge fills the left
        // panel (the slot the image would occupy) as a colored block.
        const showBadgePanel = !tier.imageUrl && !!tier.badge;

        return (
          <div
            key={tier.id}
            ref={(el) => {
              tierRefs.current[i] = el;
            }}
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            className={`${styles.tierHorizontal} ${isSelected ? styles.tierHorizontalSelected : ""}`}
            style={tierCssVars(tier)}
            onClick={() => handleClick(i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          >
            <div className={styles.tierHorizontalRow}>
              {tier.imageUrl && (
                <img
                  src={tier.imageUrl}
                  alt=""
                  className={styles.imageHorizontal}
                  aria-hidden="true"
                />
              )}

              {showBadgePanel && (
                <div
                  className={styles.badgePanelHorizontal}
                  style={{
                    color: tier.badge!.textColor ?? "#fff",
                    backgroundColor:
                      tier.badge!.backgroundColor ??
                      "var(--cod-primary-color, #000)",
                  }}
                >
                  {tier.badge!.text}
                </div>
              )}

              <div className={styles.content}>
                <span
                  className={styles.title}
                  style={
                    tier.titleColor ? { color: tier.titleColor } : undefined
                  }
                >
                  {tier.title}
                </span>
                <div className={styles.priceRow}>
                  <span
                    className={styles.price}
                    style={
                      tier.priceColor ? { color: tier.priceColor } : undefined
                    }
                  >
                    {tier.priceLabel}
                  </span>
                  {tier.compareAtLabel && (
                    <span className={styles.compareAt}>
                      {tier.compareAtLabel}
                    </span>
                  )}
                </div>
              </div>

              {/* Badge as inline chip — only shown when image is present */}
              {!showBadgePanel && tier.badge && (
                <div
                  className={styles.badgeHorizontal}
                  style={{
                    color: tier.badge.textColor ?? "#fff",
                    backgroundColor:
                      tier.badge.backgroundColor ??
                      "var(--cod-primary-color, #000)",
                  }}
                >
                  {tier.badge.text}
                </div>
              )}

              <div
                className={`${styles.radio} ${isSelected ? styles.radioSelected : ""}`}
              >
                {isSelected && <span className={styles.radioInner} />}
              </div>
            </div>

            {isSelected && renderVariantDropdowns(tier)}
          </div>
        );
      })}
    </div>
  );
};
